import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { useAuth } from '../context/AuthContext';

// Mirrors the mobile app's 3-tab bottom bar (frontend-mobile/app/(tabs)/_layout.tsx):
// Study, Manage, Account. Study/Manage aren't separate routes on web (Dashboard.js
// keeps its single-route view toggle) — tapping them drives that toggle via the
// ?view= search param, which Dashboard also reads/writes, so both stay in sync
// without lifting view state into a shared context.
function BottomNav() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (!isAuthenticated) return null;

  const isPremium = !!user?.subscription?.hasPremium;
  const onDashboard = location.pathname === '/dashboard';
  const paramView = searchParams.get('view');
  const activeView = paramView === 'study' || paramView === 'manage' ? paramView : (user?.preferredView || 'study');

  const isStudyActive = onDashboard && activeView === 'study';
  const isManageActive = onDashboard && activeView === 'manage';
  const isAccountActive = location.pathname === '/account';

  const tabClass = (active) =>
    `flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 transition-colors ${
      active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
    }`;

  // Fixed h-16 (not content-driven padding) so the total bar height — this plus the
  // 1px border-t and the safe-area inset below — is a known, exact quantity that
  // InstallBanner's bottom offset can match instead of guessing at content sizing.
  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-card border-t border-border pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch max-w-5xl mx-auto h-16">
        <Link to="/dashboard?view=study" className={tabClass(isStudyActive)} data-testid="bottom-nav-study">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
          </svg>
          <span className="text-xs font-medium truncate max-w-full">{t('nav.study')}</span>
        </Link>
        <Link to="/dashboard?view=manage" className={tabClass(isManageActive)} data-testid="bottom-nav-manage">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
          <span className="text-xs font-medium truncate max-w-full">{t('nav.manage')}</span>
        </Link>
        <Link to="/account" className={tabClass(isAccountActive)} data-testid="bottom-nav-account">
          <span className="relative">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {isPremium && (
              <svg className="absolute -top-1 -right-1 w-3 h-3 text-warning" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5z" clipRule="evenodd" />
              </svg>
            )}
          </span>
          <span className="text-xs font-medium truncate max-w-full">{t('account.mobile.heading')}</span>
        </Link>
      </div>
    </nav>
  );
}

export default BottomNav;
